import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { projectService, Project } from '../../services/project.service';
import { changeManagementService, ChangeOrderFull } from '../../services/change-management.service';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { LoadingState } from '../../components/ui/LoadingState';
import { ErrorState } from '../../components/ui/ErrorState';
import { EmptyState } from '../../components/ui/EmptyState';

function formatCurrency(n: number): string {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    maximumFractionDigits: 0,
  }).format(n || 0);
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Borrador',
  pending: 'Pendiente',
  client_approved: 'Aprobado cliente',
  approved: 'Aprobado',
  rejected: 'Rechazado',
  executed: 'Ejecutado',
};

export function ChangeOrdersPage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [orders, setOrders] = useState<ChangeOrderFull[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const canCreate = ['sys_admin', 'general_manager', 'project_lead'].includes(
    user?.role ?? ''
  );

  useEffect(() => {
    projectService
      .getProjects()
      .then((data) => {
        setProjects(data);
        if (data.length > 0 && !selectedProjectId) {
          setSelectedProjectId(data[0].id);
        }
      })
      .catch((e) => setError(e.message || 'Error al cargar proyectos'))
      .finally(() => setLoadingProjects(false));
  }, []);

  useEffect(() => {
    if (!selectedProjectId) {
      setOrders([]);
      return;
    }
    setLoadingOrders(true);
    setError(null);
    changeManagementService
      .getChangeOrders(selectedProjectId)
      .then(setOrders)
      .catch((e) => setError(e.message || 'Error al cargar órdenes'))
      .finally(() => setLoadingOrders(false));
  }, [selectedProjectId]);

  const filtered = useMemo(() => {
    if (filterStatus === 'all') return orders;
    return orders.filter((o) => o.status === filterStatus);
  }, [orders, filterStatus]);

  const stats = useMemo(() => {
    const total = orders.length;
    const pending = orders.filter((o) => o.status === 'pending').length;
    const approved = orders.filter(
      (o) => o.status === 'approved' || o.status === 'client_approved'
    ).length;
    const totalImpact = orders.reduce(
      (sum, o) => sum + (o.impact?.impact ?? 0),
      0
    );
    return { total, pending, approved, totalImpact };
  }, [orders]);

  if (loadingProjects) return <LoadingState message="Cargando proyectos…" />;

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Órdenes de Cambio</h1>
          <p className="text-xs text-slate-500">
            {projects.length} proyecto{projects.length === 1 ? '' : 's'}
          </p>
        </div>
        {canCreate && selectedProjectId && (
          <Link
            to={`/gestion/ordenes-cambio/nueva?projectId=${selectedProjectId}`}
            className="px-3 py-2 rounded-lg bg-primary text-white text-sm font-semibold"
          >
            + Nueva
          </Link>
        )}
      </div>

      {projects.length === 0 ? (
        <EmptyState icon="🏗️" title="Sin proyectos" description="Crea un proyecto para gestionar cambios." />
      ) : (
        <>
          <select
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
            className="w-full px-3 py-2.5 rounded-lg bg-white border border-slate-200 text-sm focus:border-primary focus:outline-none"
          >
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>

          <div className="grid grid-cols-4 gap-2 text-center">
            <div className="bg-white rounded-lg p-2 shadow-sm">
              <p className="text-[10px] text-slate-500">Total</p>
              <p className="text-lg font-bold">{stats.total}</p>
            </div>
            <div className="bg-white rounded-lg p-2 shadow-sm">
              <p className="text-[10px] text-slate-500">Pendientes</p>
              <p className="text-lg font-bold text-amber-600">{stats.pending}</p>
            </div>
            <div className="bg-white rounded-lg p-2 shadow-sm">
              <p className="text-[10px] text-slate-500">Aprobados</p>
              <p className="text-lg font-bold text-emerald-600">{stats.approved}</p>
            </div>
            <div className="bg-white rounded-lg p-2 shadow-sm">
              <p className="text-[10px] text-slate-500">Impacto</p>
              <p className="text-sm font-bold text-primary">
                {formatCurrency(stats.totalImpact)}
              </p>
            </div>
          </div>

          <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-4 px-4">
            {['all', 'pending', 'client_approved', 'approved', 'rejected', 'executed'].map(
              (s) => (
                <button
                  key={s}
                  onClick={() => setFilterStatus(s)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${
                    filterStatus === s
                      ? 'bg-primary text-white'
                      : 'bg-white text-slate-600 border border-slate-200'
                  }`}
                >
                  {s === 'all' ? 'Todas' : STATUS_LABELS[s]}
                </button>
              )
            )}
          </div>

          {loadingOrders ? (
            <LoadingState />
          ) : error ? (
            <ErrorState message={error} />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon="🔄"
              title="Sin órdenes"
              description={
                orders.length === 0
                  ? 'Crea la primera orden de cambio para este proyecto.'
                  : 'No hay órdenes con este filtro.'
              }
            />
          ) : (
            <ul className="space-y-3">
              {filtered.map((o) => (
                <li key={o.id}>
                  <Link
                    to={`/gestion/ordenes-cambio/${o.id}`}
                    className="block bg-white rounded-xl shadow-sm p-4 active:bg-slate-50"
                  >
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="min-w-0">
                        <p className="text-[11px] font-mono text-slate-500">
                          {o.number}
                        </p>
                        <h3 className="text-sm font-semibold text-slate-900 truncate">
                          {o.title || o.description}
                        </h3>
                      </div>
                      <StatusBadge status={o.status} label={STATUS_LABELS[o.status]} />
                    </div>
                    <div className="flex items-center justify-between gap-2 text-xs">
                      <span className="text-slate-500 truncate">
                        {o.requestedByName ?? '—'}
                      </span>
                      <span
                        className={`font-semibold ${
                          (o.impact?.impact ?? 0) >= 0
                            ? 'text-red-600'
                            : 'text-emerald-600'
                        }`}
                      >
                        {formatCurrency(o.impact?.impact ?? 0)}
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}